import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { H4Normal } from "../../../../../components/Typography/Typography";
import {
  Flex,
  Flex1,
  Flex2,
  RadioButtonContainer,
} from "../../../../../layout/FormLayout";
import { RadioButton } from "../../../../../components/RadioButton/RadioButton";
import type { Dispatch, SetStateAction } from "react";
import type { FieldError } from "react-hook-form";
import { Controller } from "react-hook-form";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { getPhoneCodesOptions } from "../../../../../util/phone";
import type {
  MethodsOfUseForm,
  OptionType,
  User,
} from "../../../../../types/types";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { convertUserToOption, useStoreState } from "../../../../../util/util";
import { endpoints } from "../../../../../endpoints";
import type { TwoLineOption } from "../../../../../components/SelectBoxV2/TwoLineOptionLabel";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  width: 100%;
`;

/**
 * Option for an existing-user POC. `label` is what the dropdown shows, while
 * `primaryText`/`supportingText` drive the POC row header: the name, then
 * "Email • Business Unit" with whichever of the two is populated.
 */
export const existingUserPointOfContactOption = ({
  id,
  name,
  label,
  email_address,
  business_unit,
  country,
}: {
  id: string;
  name: string;
  label: string;
  email_address?: string | null;
  business_unit?: string | null;
  country?: string | null;
}): TwoLineOption => {
  const businessUnit =
    business_unit && country ? `${business_unit} (${country})` : business_unit;
  const supportingText = [email_address, businessUnit]
    .filter((part) => part?.trim())
    .join(" • ");
  return {
    value: id,
    label,
    primaryText: name.trim() || undefined,
    supportingText: supportingText || undefined,
  };
};

const userToPointOfContactOption = (user: User) =>
  existingUserPointOfContactOption({
    id: user.id,
    name: `${user.firstname ?? ""} ${user.lastname ?? ""}`,
    label: convertUserToOption(user).label,
    email_address: user.email_address,
    business_unit: user.business_unit,
    country: user.country,
  });

export const PointOfContact = ({
  methodsOfUseForm,
  pointOfContactType,
  setPointOfContactType,
  defaultExistingUser,
  fieldNamePrefix,
  rowErrors,
  excludeUserIds,
  hideHeading = false,
  disabled = false,
}: {
  methodsOfUseForm: MethodsOfUseForm;
  pointOfContactType: "manual" | "existing";
  defaultExistingUser: User;
  setPointOfContactType: Dispatch<SetStateAction<"manual" | "existing">>;
  /** e.g. "points_of_contact[2]" when this is one row of a POC field array. */
  fieldNamePrefix?: string;
  /**
   * This row's slice of the form errors (e.g. `errors.points_of_contact?.[2]`),
   * needed because RHF's `errors[name]` lookup can't resolve a nested
   * array-index path like "points_of_contact[2].email_address" on its own.
   */
  rowErrors?: Record<string, FieldError | undefined>;
  /** User ids already picked as a POC elsewhere on this location, excluded from the "Existing User" dropdown. */
  excludeUserIds?: string[];
  hideHeading?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { control, errors, formState, register, watch } = methodsOfUseForm;

  const fieldName = (name: string) =>
    fieldNamePrefix ? `${fieldNamePrefix}.${name}` : name;

  // The currently selected existing-user option, derived straight from the
  // form's live value rather than mirrored into separate state — RHF's
  // `watch` already returns a fresh value on every relevant change, so
  // copying it into `useState`/`useEffect` only risked a render loop when
  // the watched value's identity changed on every render.
  const pointOfContactValue: TwoLineOption | undefined = watch(
    fieldName("point_of_contact_id")
  );
  // `defaultExistingUser` is sometimes a real user to preselect (e.g. the
  // tenant's primary contact) and sometimes just a `{ tenant_id }` stub used
  // to scope the search API for a brand-new row with nothing selected yet.
  // Only convert it to an option when it's an actual user — otherwise
  // `convertUserToOption` renders "undefined undefined" for the missing
  // name fields, which then leaks into the accordion row's title.
  const selectedUser =
    pointOfContactValue ??
    (defaultExistingUser.id
      ? userToPointOfContactOption(defaultExistingUser)
      : undefined);

  // The manual-entry fields below are uncontrolled inputs (`ref={register()}`),
  // so they only read their initial value once, when they mount — collapsing
  // the accordion or switching the radio away and back unmounts them, and a
  // fresh mount would otherwise fall back to the form's original defaultValues
  // (blank, for a new row) instead of whatever the user had typed. The form is
  // set up with `shouldUnregister: false`, so RHF keeps tracking each field's
  // last value even while unmounted — feeding that back in as `defaultValue`
  // on remount is what actually restores it in the DOM.
  const countryCodeValue: OptionType | undefined = watch(
    fieldName("country_code")
  );

  const changePointOfContact = (e: React.FormEvent<HTMLSelectElement>) => {
    const pocType = e.currentTarget.value as "manual" | "existing";
    if (e.currentTarget.value) {
      setPointOfContactType(pocType);
    }
  };

  return (
    <Stack>
      {!hideHeading && <H4Normal>{t("Point of Contact")}</H4Normal>}
      <RadioButtonContainer>
        <RadioButton
          name={fieldName("existingUser")}
          value="existing"
          checked={pointOfContactType === "existing"}
          optionTitle="Existing User"
          handleChange={changePointOfContact}
          disabled={disabled}
        />
        <RadioButton
          name={fieldName("manualUser")}
          value="manual"
          checked={pointOfContactType === "manual"}
          optionTitle="Add Manually"
          handleChange={changePointOfContact}
          disabled={disabled}
        />
      </RadioButtonContainer>
      {pointOfContactType === "existing" && (
        <Controller
          as={SearchSelectInfiniteScroll}
          isClearable
          control={control}
          name={fieldName("point_of_contact_id")}
          autoComplete="point_of_contact_id"
          baseUrl={
            storefront_id
              ? endpoints.v1_storefronts_id_tenants_id_users(
                  storefront_id,
                  defaultExistingUser.tenant_id
                )
              : null
          }
          params={(() => {
            const params = new URLSearchParams();
            params.append("offset", "0");
            params.append("order_by", "asc");
            excludeUserIds?.forEach((id) =>
              params.append("exclude_user_ids", id)
            );
            return params;
          })()}
          placeholder={t("Select User")}
          getOptions={(response: User[]) =>
            response.reduce(
              (prev: TwoLineOption[], user) =>
                user.is_active
                  ? [...prev, userToPointOfContactOption(user)]
                  : prev,
              []
            )
          }
          testid={fieldName("point_of_contact_id")}
          defaultOptions
          defaultValue={selectedUser}
          error={rowErrors?.point_of_contact_id}
          errors={errors}
          formState={formState}
          isDisabled={disabled}
        />
      )}
      {pointOfContactType === "manual" && (
        <>
          <TextField
            name={fieldName("contact_first_name")}
            label={t("First Name")}
            theref={register({
              required: true,
            })}
            formState={formState}
            error={rowErrors?.contact_first_name}
            errors={errors}
            type="text"
            defaultValue={watch(fieldName("contact_first_name"))}
            readOnly={disabled}
          />
          <TextField
            name={fieldName("contact_last_name")}
            label={t("Last Name")}
            theref={register({
              required: true,
            })}
            formState={formState}
            error={rowErrors?.contact_last_name}
            errors={errors}
            type="text"
            defaultValue={watch(fieldName("contact_last_name"))}
            readOnly={disabled}
          />
          <TextField
            name={fieldName("email_address")}
            autoComplete="email"
            label={t("Email")}
            theref={register({
              required: true,
            })}
            formState={formState}
            error={rowErrors?.email_address}
            errors={errors}
            type="email"
            defaultValue={watch(fieldName("email_address"))}
            readOnly={disabled}
          />
          <Flex>
            <Flex1>
              <Controller
                as={SelectBoxV2}
                control={control}
                name={fieldName("country_code")}
                autoComplete="countryCode"
                placeholder={t("Country Code")}
                id="countryCodeSelectBox"
                options={getPhoneCodesOptions()}
                rules={{
                  required: true,
                }}
                defaultValue={countryCodeValue ?? { label: "", value: "" }}
                error={rowErrors?.country_code}
                errors={errors}
                formState={formState}
                disabled={disabled}
              />
            </Flex1>
            <Flex2 style={{ marginRight: 0, marginLeft: "14px" }}>
              <TextField
                name={fieldName("phone_number")}
                autoComplete="tel"
                label={t("Phone Number")}
                theref={register({
                  required: true,
                })}
                formState={formState}
                error={rowErrors?.phone_number}
                errors={errors}
                type="tel"
                defaultValue={watch(fieldName("phone_number"))}
                readOnly={disabled}
              />
            </Flex2>
          </Flex>
        </>
      )}
    </Stack>
  );
};
